/** Russian words for the backend's slugs (WP-16): operators read «Владелец» and «Одно
 *  устройство», not "owner" and "same_device". Pure, no I/O. An unknown slug falls back to the
 *  slug itself with `known: false`, so a page shows it quietly (ink-3) instead of inventing a
 *  word — a new backend value is visible, never hidden. Statuses with a tone live in status.ts. */
import { fmtCredits, fmtDecimal } from '@/lib/format'

export interface Label {
  label: string
  known: boolean
}

function lookup(table: Record<string, string>, slug: string | null | undefined): Label {
  if (slug === null || slug === undefined || slug === '') return { label: '—', known: true }
  const hit = table[slug]
  return hit !== undefined ? { label: hit, known: true } : { label: slug, known: false }
}

/** Tiers are product names (the bot and the Mini App say «Pro»), only capitalised. */
export const TIER: Record<string, string> = { free: 'Free', basic: 'Basic', pro: 'Pro', elite: 'Elite' }
export const tierLabel = (slug: string | null | undefined): Label => lookup(TIER, slug)

export const ROLE: Record<string, string> = {
  owner: 'Владелец',
  admin: 'Администратор',
  finance: 'Финансист',
  support: 'Поддержка',
  analyst: 'Аналитик',
}
export const roleLabel = (slug: string | null | undefined): Label => lookup(ROLE, slug)

/** Antifraud flags on a referral earning (src/core/referral/service.py and the fixtures). */
export const FRAUD_FLAG: Record<string, string> = {
  velocity: 'Много начислений за час',
  same_device: 'Одно устройство',
  fast_payment_after_signup: 'Оплата сразу после регистрации',
  self_referral: 'Пригласил сам себя или общий адрес с рефералом',
  device_cluster: 'Много аккаунтов на одном устройстве',
  fresh_referrals: 'Комиссия в основном от новых аккаунтов',
  instant_withdrawal: 'Вывод сразу после начисления',
  global_daily_limit: 'Превышен общий суточный лимит выплат',
  address_changed: 'Новый адрес выплаты',
}
export const fraudFlagLabel = (slug: string): Label => lookup(FRAUD_FLAG, slug)

/** `ledger_entries.entry_type` (LedgerEntryType in src/core/models/enums.py). */
export const LEDGER_ENTRY: Record<string, string> = {
  topup: 'Пополнение',
  hold: 'Удержание',
  hold_release: 'Возврат удержания',
  charge: 'Списание',
  refund: 'Возврат',
  bonus: 'Бонус',
  referral: 'Реферальное начисление',
  admin_adjust: 'Корректировка оператором',
  subscription: 'Подписка',
  debt_payment: 'Погашение долга',
  gift_send: 'Подарок отправлен',
  gift_receive: 'Подарок получен',
  gift_refund: 'Возврат подарка',
}
export const ledgerEntryLabel = (slug: string): Label => lookup(LEDGER_ENTRY, slug)

/** `admin_audit_log.action` → what happened, in words. */
export const AUDIT_ACTION: Record<string, string> = {
  'auth.login': 'Вход в админку',
  'auth.login_failed': 'Неудачный вход',
  'auth.miniapp_login': 'Вход через Telegram',
  'auth.miniapp_login_failed': 'Неудачный вход через Telegram',
  'auth.miniapp_challenge': 'Запрошен вход через Telegram',
  'admin.create': 'Добавлен оператор',
  'admin.create_password': 'Добавлен оператор с паролем',
  'admin.update': 'Изменён оператор',
  'pricing.update.model': 'Изменена модель',
  'pricing.update.global': 'Изменены общие настройки цен',
  'pricing.update.tier': 'Изменён тариф',
  'pricing.update.package': 'Изменён пакет',
  'pricing.update.plan': 'Изменён план подписки',
  'pricing.bulk.model': 'Модели изменены пакетом',
  'pricing.bulk.price': 'Цены изменены пакетом',
  'pricing.menu_order': 'Изменён порядок моделей в меню',
  'pricing.fx.refresh': 'Обновлён курс',
  'pricing.sync': 'Цены провайдера загружены',
  'user.credit': 'Начислены кредиты',
  'users.credit': 'Начислены кредиты',
  'user.ban': 'Пользователь заблокирован',
  'user.unban': 'Пользователь разблокирован',
  'user.erase_pii': 'Стёрты личные данные',
  'user.price_override': 'Персональная цена',
  'user.reset_debt': 'Списан долг',
  'user.set_tier': 'Изменён тариф пользователя',
  'user.tag.set': 'Поставлена метка',
  'user.tag.unset': 'Снята метка',
  'payment.refund': 'Возврат платежа',
  'payout.approve': 'Выплата одобрена',
  'payout.reject': 'Выплата отклонена',
  'payout.retry': 'Выплата отправлена повторно',
  'payout.return': 'Резерв возвращён партнёру',
  'referral.clear_flag': 'Снят антифрод-флаг',
  'promo.create': 'Создан промокод',
  'promo.update': 'Изменён промокод',
  'promo.toggle': 'Промокод включён или выключен',
  'broadcast.create': 'Создана рассылка',
  'broadcast.schedule': 'Рассылка запланирована',
  'broadcast.send': 'Рассылка отправлена',
  'content.create': 'Добавлен контент',
  'content.update': 'Изменён контент',
  'content.delete': 'Удалён контент',
  'content.media.add': 'Добавлен пример',
  'content.media.update': 'Изменён пример',
  'content.media.delete': 'Удалён пример',
  'contacts.update': 'Изменены контакты',
  'contacts.bot_profile.apply': 'Профиль бота обновлён',
  'support.reply': 'Ответ в поддержке',
  'support.status': 'Изменён статус обращения',
  'gifts.cancel': 'Подарок отменён',
  'subscriptions.autorenew.cancel': 'Автопродление отменено',
  'job.retry': 'Генерация повторена',
  'system.flag.set': 'Изменён флаг',
  'system.provider_key.toggle': 'Ключ провайдера включён или выключен',
  'system.secrets.update': 'Изменены ключи интеграции',
  'system.secrets.pool_add': 'Добавлен ключ в пул',
  'system.secrets.pool_remove': 'Убран ключ из пула',
  'system.mcp.connect': 'Подключён MCP',
  'system.mcp.disconnect': 'Отключён MCP',
  'system.mcp.tools_refresh': 'Обновлены инструменты MCP',
  'payments.gateways.update': 'Изменены шлюзы СБП',
  'payments.method_settings.update': 'Изменены способы оплаты',
  'accounting.expense.create': 'Добавлен расход',
  'accounting.expense.delete': 'Удалён расход',
  'accounting.fee_rule.create': 'Добавлено правило комиссии',
  'accounting.fee_rule.delete': 'Удалено правило комиссии',
  'accounting.partner.create': 'Добавлен партнёр',
  'accounting.partner.update': 'Изменён партнёр',
  'accounting.partner.delete': 'Удалён партнёр',
  'accounting.distribution.create': 'Создано распределение',
  'accounting.distribution.approve': 'Распределение одобрено',
  'accounting.distribution.delete': 'Распределение удалено',
  'accounting.share.paid': 'Доля выплачена',
  'acquisition.campaign.create': 'Создана кампания',
  'acquisition.campaign.update': 'Изменена кампания',
  'acquisition.channel.create': 'Добавлен канал',
  'acquisition.channel.update': 'Изменён канал',
  'acquisition.targets.update': 'Изменены цели привлечения',
  'refcontest.settings': 'Изменён конкурс рефералов',
  'refcontest.settle': 'Конкурс рефералов подведён',
}
export const auditActionLabel = (slug: string): Label => lookup(AUDIT_ACTION, slug)

/** Sections of the audit log for the «Действие» filter: the slugs a section holds (by prefix)
 *  and, where one part of the slug names them all, that part — the server filter matches a
 *  part of the slug (`payout.` → every payout action). A section without `match` has no
 *  «все действия» option: no single part names only its slugs (`ref` would also match
 *  `pricing.fx.refresh`). */
export const AUDIT_SECTIONS: readonly { label: string; prefixes: readonly string[]; match?: string }[] = [
  { label: 'Вход', prefixes: ['auth.'], match: 'auth.' },
  { label: 'Операторы', prefixes: ['admin.'], match: 'admin.' },
  { label: 'Цены и каталог', prefixes: ['pricing.'], match: 'pricing.' },
  { label: 'Пользователи', prefixes: ['user.', 'users.'], match: 'user' },
  { label: 'Платежи', prefixes: ['payment.', 'payments.'], match: 'payment' },
  { label: 'Выплаты', prefixes: ['payout.'], match: 'payout.' },
  { label: 'Партнёрка', prefixes: ['referral.', 'refcontest.'] },
  { label: 'Промокоды', prefixes: ['promo.'], match: 'promo.' },
  { label: 'Рассылки', prefixes: ['broadcast.'], match: 'broadcast.' },
  { label: 'Контент', prefixes: ['content.'], match: 'content.' },
  { label: 'Контакты и ссылки', prefixes: ['contacts.'], match: 'contacts.' },
  { label: 'Поддержка', prefixes: ['support.'], match: 'support.' },
  { label: 'Подарки и автопродление', prefixes: ['gifts.', 'subscriptions.'] },
  { label: 'Генерации', prefixes: ['job.'], match: 'job.' },
  { label: 'Система', prefixes: ['system.'], match: 'system.' },
  { label: 'Бухгалтерия', prefixes: ['accounting.'], match: 'accounting.' },
  { label: 'Привлечение', prefixes: ['acquisition.'], match: 'acquisition.' },
]

/** A «Действие» filter value: a whole section (`section:<part of the slug>`) or one action
 *  (its slug, matched exactly). */
export type AuditActionFilter = { kind: 'section'; match: string; label: string } | { kind: 'action'; slug: string; label: string }

const SECTION = 'section:'

/** The «Действие» options: «Все действия», then per section «<Раздел>: все действия» and its
 *  actions in Russian (value = the slug). Every known action is in exactly one section. */
export function auditActionOptions(): { value: string; label: string; group?: string }[] {
  const out: { value: string; label: string; group?: string }[] = [{ value: '', label: 'Все действия' }]
  for (const s of AUDIT_SECTIONS) {
    const slugs = Object.keys(AUDIT_ACTION).filter((k) => s.prefixes.some((p) => k.startsWith(p)))
    if (slugs.length === 0) continue
    if (s.match) out.push({ value: `${SECTION}${s.match}`, label: `${s.label}: все действия`, group: s.label })
    for (const slug of slugs) out.push({ value: slug, label: AUDIT_ACTION[slug] ?? slug, group: s.label })
  }
  return out
}

/** What a «Действие» option value means: the query (`action`, `exact`) and the chip's words. */
export function auditActionFilter(value: string): AuditActionFilter | null {
  if (!value) return null
  if (value.startsWith(SECTION)) {
    const match = value.slice(SECTION.length)
    const section = AUDIT_SECTIONS.find((s) => s.match === match)
    return { kind: 'section', match, label: section ? `${section.label}: все действия` : match }
  }
  return { kind: 'action', slug: value, label: auditActionLabel(value).label }
}

/** `admin_audit_log.target_type` */
export const TARGET_TYPE: Record<string, string> = {
  model: 'Модель',
  model_kind: 'Категория моделей',
  user: 'Пользователь',
  payment: 'Платёж',
  payout: 'Выплата',
  promo: 'Промокод',
  broadcast: 'Рассылка',
  content: 'Контент',
  admin: 'Оператор',
  tier: 'Тариф',
  plan: 'План',
  package: 'Пакет',
  ticket: 'Обращение',
  campaign: 'Кампания',
  channel: 'Канал',
  flag: 'Флаг',
  key: 'Ключ',
}
export const targetTypeLabel = (slug: string | null | undefined): Label => lookup(TARGET_TYPE, slug)

/** How a field value reads: credits get ⚡ (decision D8), money and plain numbers are grouped,
 *  booleans are words. */
type Kind = 'credits' | 'number' | 'bool' | 'text' | 'tier' | 'role'

/** Fields of audit `before`/`after` and of activity details: name and how to read the value. */
export const FIELD: Record<string, { label: string; kind: Kind }> = {
  base_credits: { label: 'Цена', kind: 'credits' },
  credits: { label: 'Кредиты', kind: 'credits' },
  balance: { label: 'Баланс', kind: 'credits' },
  amount: { label: 'Сумма', kind: 'number' },
  amount_usdt: { label: 'Сумма, USDT', kind: 'number' },
  amount_rub: { label: 'Сумма, ₽', kind: 'number' },
  price_rub: { label: 'Цена, ₽', kind: 'number' },
  markup: { label: 'Наценка', kind: 'number' },
  global_markup: { label: 'Общая наценка', kind: 'number' },
  is_enabled: { label: 'Включена', kind: 'bool' },
  enabled: { label: 'Включено', kind: 'bool' },
  is_active: { label: 'Активен', kind: 'bool' },
  is_banned: { label: 'Заблокирован', kind: 'bool' },
  status: { label: 'Статус', kind: 'text' },
  reviewed_by: { label: 'Проверил', kind: 'text' },
  reject_reason: { label: 'Причина отказа', kind: 'text' },
  reason: { label: 'Причина', kind: 'text' },
  tier: { label: 'Тариф', kind: 'tier' },
  min_tier: { label: 'Минимальный тариф', kind: 'tier' },
  role: { label: 'Роль', kind: 'role' },
  model: { label: 'Модель', kind: 'text' },
  model_code: { label: 'Модель', kind: 'text' },
  code: { label: 'Код', kind: 'text' },
  title: { label: 'Название', kind: 'text' },
  method: { label: 'Способ', kind: 'text' },
  provider: { label: 'Провайдер', kind: 'text' },
  source: { label: 'Источник', kind: 'text' },
  count: { label: 'Количество', kind: 'number' },
  days: { label: 'Дней', kind: 'number' },
  attempts: { label: 'Попыток', kind: 'number' },
}

const DECIMAL = /^-?\d+(\.\d+)?$/

/** A field's value as an operator reads it: «24 ⚡», «1 204,50», «да». */
export function fieldValue(key: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  const kind = FIELD[key]?.kind ?? 'text'
  if (typeof v === 'boolean') return v ? 'да' : 'нет'
  const s = typeof v === 'number' ? String(v) : typeof v === 'string' ? v : JSON.stringify(v)
  if (kind === 'credits' && DECIMAL.test(s)) return fmtCredits(s)
  if (kind === 'tier') return tierLabel(s).label
  if (kind === 'role') return roleLabel(s).label
  if (DECIMAL.test(s) && (kind === 'number' || s.includes('.') || s.length > 4)) return fmtDecimal(s, { max: 6 })
  return s
}

export const fieldLabel = (key: string): Label => lookup(Object.fromEntries(Object.entries(FIELD).map(([k, f]) => [k, f.label])), key)

export interface FieldChange {
  key: string
  label: Label
  before: string
  after: string
  /** the field is new (no «before») or gone (no «after») */
  only?: 'before' | 'after'
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null
}

/** The fields an audit entry changed, in words: «Цена 22 ⚡ → 24 ⚡». Unchanged fields are left
 *  out. A value that is not an object (a bare number) is one row named «Значение». */
export function auditChanges(before: unknown, after: unknown): FieldChange[] {
  const b = asRecord(before)
  const a = asRecord(after)
  if (b === null && a === null) {
    if (before === after || (before == null && after == null)) return []
    return [
      { key: 'value', label: { label: 'Значение', known: true }, before: fieldValue('value', before), after: fieldValue('value', after) },
    ]
  }
  const keys = [...new Set([...Object.keys(b ?? {}), ...Object.keys(a ?? {})])]
  const out: FieldChange[] = []
  for (const key of keys) {
    const bv = b?.[key]
    const av = a?.[key]
    if (JSON.stringify(bv) === JSON.stringify(av)) continue
    out.push({
      key,
      label: fieldLabel(key),
      before: fieldValue(key, bv),
      after: fieldValue(key, av),
      only: b === null || !(key in b) ? 'after' : a === null || !(key in a) ? 'before' : undefined,
    })
  }
  return out
}
